import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { store } from '@/store/store';
import { hubClient } from '@tfpl/api-client';
import { getConfig } from '@/config';

export interface UserUuidParam {
  collaboratorUuid?: string;
}

export interface AbsenceFilterParams extends UserUuidParam {
  type?: hubClient.OfficeAbsencesDto['type'];
  dateFrom?: string;
  dateTo?: string;
  states?: hubClient.OfficeAbsencesDto['state'][];
}

export interface AbsenceFilterStatesParams extends AbsenceFilterParams {
  states?: hubClient.OfficeAbsencesDto['state'][];
}

export interface Approver {
  id: number | null;
  uuid: string | null;
  avatarUrl: string;
  fullName: string | null;
  priority: boolean | null;
}

export interface CancelOfficeAbsence extends UserUuidParam {
  absenceId: number;
  approverId: number;
}

export interface OfficeAbsencesStatistics {
  totalHoliday: number;
  annualHolidayInPercent: number;
  totalHO: number;
  annualHOInPercent: number;
  totalIllness: number;
  annualIllnessInPercent: number;
  totalInaccessibility: number;
  annualInaccessibilityInPercent: number;
}

export interface OfficeAbsence {
  id: number;
  author: hubClient.MinimalCollaboratorDto;
  start: string;
  end: string;
  created: string;
  type: hubClient.OfficeAbsencesDto['type'];
  state: hubClient.OfficeAbsencesDto['state'];
  approver: Approver;
  hoursNumber: number;
  generalDescription: string;
  approverDescription: string;
  isLateRequest: boolean;
  lateRequestMissingDays: number | null;
  isWeeklyLimitException: boolean;
}

const transformOfficeAbsence = (absence: hubClient.OfficeAbsencesDto): OfficeAbsence => {
  return {
    id: absence.id,
    author: absence.collaborator,
    start: absence.dateFrom,
    end: absence.dateTo,
    created: absence.createdAt,
    type: absence.type,
    state: absence.state,
    approver: {
      fullName: absence.approver ? `${absence.approver.firstName} ${absence.approver.lastName}` : '',
      id: absence.approver ? absence.approver.id : null,
      uuid: absence.approver ? absence.approver.uuid : null,
      avatarUrl: absence.approver.slackAvatarUrl,
      priority: false,
    },
    hoursNumber: absence.hours,
    generalDescription: absence.generalDescription,
    approverDescription: absence.approverDescription,
    isLateRequest: absence.isLateRequest ?? false,
    lateRequestMissingDays: absence.lateRequestMissingDays ?? null,
    isWeeklyLimitException: absence.isWeeklyLimitException ?? false,
  };
};

@Module({ name: 'OfficeAbsenceModule', store, dynamic: true, namespaced: true })
export class OfficeAbsenceModule extends VuexModule {
  /**************************************************************************
   * STATE
   *************************************************************************/
  private _loading = false;
  private _error = false;
  private _approvers: Approver[] = [];
  private _absences: OfficeAbsence[] = [];
  private _absencesForApprove: OfficeAbsence[] = [];
  private _statiscs: OfficeAbsencesStatistics = {
    totalHoliday: 0,
    annualHolidayInPercent: 0,
    totalHO: 0,
    annualHOInPercent: 0,
    totalIllness: 0,
    annualIllnessInPercent: 0,
    totalInaccessibility: 0,
    annualInaccessibilityInPercent: 0,
  };

  /**************************************************************************
   * GETTERS
   *************************************************************************/
  get absences(): OfficeAbsence[] {
    return this._absences;
  }

  get absencesForApprove(): OfficeAbsence[] {
    return this._absencesForApprove;
  }

  get approvers(): Approver[] {
    return this._approvers;
  }

  get loading(): boolean {
    return this._loading;
  }

  get error(): boolean {
    return this._error;
  }
  get statiscs(): OfficeAbsencesStatistics {
    return this._statiscs;
  }
  /**************************************************************************
   * MUTATIONS
   *************************************************************************/
  @Mutation
  clearAbsences(): void {
    this._absences = [];
  }

  @Mutation
  clearAbsencesForApprove(): void {
    this._absencesForApprove = [];
  }

  @Mutation
  clearApprovers(): void {
    this._approvers = [];
  }

  @Mutation
  setAbsences(absenceItems: OfficeAbsence[]): void {
    this._absences = absenceItems;
  }

  @Mutation
  setAbsencesForApprove(absencesForApproveItems: OfficeAbsence[]): void {
    this._absencesForApprove = absencesForApproveItems;
  }

  @Mutation
  setStatistics(statiscs: OfficeAbsencesStatistics): void {
    this._statiscs = statiscs;
  }

  @Mutation
  addApprover(approver: Approver): void {
    if (!this._approvers.some((item) => item.id === approver.id)) {
      this._approvers.push(approver);
    }
  }

  @Mutation
  setLoading(loading: boolean): void {
    this._loading = loading;
  }

  @Mutation
  setError(error: boolean): void {
    this._error = error;
  }

  /**************************************************************************
   * ACTIONS
   *************************************************************************/
  @Action({ rawError: true })
  public async fetchOfficeAbsences(params: AbsenceFilterStatesParams): Promise<void> {
    const filterParams = { ...params };
    this.setLoading(true);
    try {
      // Key order matters: axios serializes query params in insertion order, and the login
      // E2E intercept matches `?limit=999&offset=0&states=...&states=...&collaboratorUuid=*`.
      // collaboratorUuid must therefore be inserted AFTER states.
      const query: hubClient.GetManyV39Data['query'] = {
        limit: 999,
        offset: 0,
      };

      if (filterParams.type) {
        query.type = filterParams.type;
      }
      if (filterParams.states) {
        query.states = filterParams.states;
      }
      query.collaboratorUuid = filterParams.collaboratorUuid;
      if (filterParams.dateFrom) {
        query.dateFrom = filterParams.dateFrom;
      }
      if (filterParams.dateTo) {
        query.dateTo = filterParams.dateTo;
      }

      const { data: response } = await hubClient.V3OfficeAbsencesService.getManyV3({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
        query,
        throwOnError: true,
      });
      const absences = response.data.map(transformOfficeAbsence);
      this.setAbsences(absences);
      await this.getAbsencesStatistics();
    } catch (err) {
      this.setError(true);
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  public async fetchOfficeAbsencesForApprove(params: AbsenceFilterParams = {}): Promise<void> {
    const filterParams = { ...params };
    this.setLoading(true);
    try {
      const query: hubClient.GetManyV39Data['query'] = {
        limit: 999,
        offset: 0,
        collaboratorUuid: filterParams.collaboratorUuid,
      };

      query.states = [
        hubClient.OfficeAbsenceState.WAITING_FOR_APPROVAL,
        hubClient.OfficeAbsenceState.AWAITING_CANCELLATION_APPROVAL,
      ];
      if (filterParams.type) {
        query.type = filterParams.type;
      }

      if (filterParams.dateFrom) {
        query.dateFrom = filterParams.dateFrom;
      }
      if (filterParams.dateTo) {
        query.dateTo = filterParams.dateTo;
      }

      const { data: response } = await hubClient.V3OfficeAbsencesService.getManyV3({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
        query,
        throwOnError: true,
      });
      const absences = response.data.map(transformOfficeAbsence);
      this.setAbsencesForApprove(absences);
    } catch (err) {
      this.setError(true);
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  public async fetchBackupApprover(): Promise<void> {
    this.setLoading(true);
    try {
      const { data: response } = await hubClient.V3OfficeAbsencesService.getBackupApproverV3({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
        throwOnError: true,
      });
      const approver = response.data.backupAbsenceApprover;

      if (approver) {
        this.addApprover({
          id: approver.id,
          uuid: approver.uuid,
          fullName: `${approver.firstName} ${approver.lastName}`,
          priority: false,
          avatarUrl: approver.slackAvatarUrl,
        });
      } else {
        console.error('Can not set backup approver');
      }
    } catch (err) {
      this.setError(true);
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  }

  @Action({ rawError: true })
  public async getAbsencesStatistics(): Promise<void> {
    this.setLoading(true);
    try {
      const { data: response } = await hubClient.V3OfficeAbsencesService.getStatisticsV3({
        path: { tenant: getConfig().TENANT, company: getConfig().TENANT },
        query: { fullYear: new Date().getFullYear() },
        throwOnError: true,
      });
      const statiscs = response.data;
      const transformStats = {
        totalHoliday: statiscs.totalHoliday,
        annualHolidayInPercent: statiscs.annualHolidayInPercent,
        totalHO: statiscs.totalHO,
        annualHOInPercent: statiscs.annualHOInPercent,
        totalIllness: statiscs.totalIllness,
        annualIllnessInPercent: statiscs.annualIllnessInPercent,
        totalInaccessibility: statiscs.totalInaccessibility,
        annualInaccessibilityInPercent: statiscs.annualInaccessibilityInPercent,
      };

      this.setStatistics(transformStats);
    } catch (err) {
      this.setError(true);
      console.error(err);
    } finally {
      this.setLoading(false);
    }
  }
}
export default getModule(OfficeAbsenceModule);
